import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Trophy, Ticket, ArrowLeft, Loader2, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/my-tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group tickets by raffle
  const groupedTickets = tickets.reduce((acc, ticket) => {
    if (!acc[ticket.raffle_id]) {
      acc[ticket.raffle_id] = {
        raffle_id: ticket.raffle_id,
        raffle_title: ticket.raffle_title,
        raffle_status: ticket.raffle_status,
        prize_amount: ticket.prize_amount,
        tickets: []
      };
    }
    acc[ticket.raffle_id].tickets.push(ticket);
    return acc;
  }, {});

  const raffleGroups = Object.values(groupedTickets);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="flex items-center gap-1 text-green-700 bg-green-100 px-2 py-1 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            Confirmed
          </span>
        );
      case 'reserved':
        return (
          <span className="flex items-center gap-1 text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-gray-600 bg-gray-100 px-2 py-1 rounded-full text-xs">
            <AlertCircle className="w-3 h-3" />
            {status}
          </span>
        );
    }
  };

  const getRaffleStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            Active
          </span>
        );
      case 'drawn':
        return (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            Drawn
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            Closed
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 mx-auto">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
              
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-black text-gray-900 mb-8" data-testid="my-tickets-title">
          My Tickets
        </h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
          </div>
        ) : raffleGroups.length === 0 ? (
          <div className="text-center py-20">
            <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No tickets yet</h3>
            <p className="text-gray-500 mb-6">Participate in a free draw to get your lucky tickets!</p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 bg-[#EAB308] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#CA8A04] transition-colors shadow-lg shadow-[#EAB308]/25"
              data-testid="browse-raffles-button"
            >
              Browse Free Draws
            </Link>
          </div>
        ) : (
          <div className="space-y-6" data-testid="tickets-list">
            {raffleGroups.map(group => (
              <div 
                key={group.raffle_id}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
              >
                {/* Raffle Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <Link 
                        to={`/raffle/${group.raffle_id}`}
                        className="font-heading text-xl font-bold text-gray-900 hover:text-[#EAB308] transition-colors"
                      >
                        {group.raffle_title}
                      </Link>
                      <p className="text-[#EAB308] font-bold mt-1">Prize: £{group.prize_amount.toLocaleString()}</p>
                    </div>
                    {getRaffleStatusBadge(group.raffle_status)}
                  </div>
                </div>

                {/* Tickets */}
                <div className="p-6">
                  <div className="flex flex-wrap gap-3">
                    {group.tickets.map(ticket => (
                      <div 
                        key={ticket.ticket_id}
                        className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-3"
                      >
                        <span className="text-[#EAB308] font-mono font-bold">
                          #{ticket.ticket_number}
                        </span>
                        {getStatusBadge(ticket.status)}
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-gray-500 text-sm mt-4">
                    Total: {group.tickets.length} ticket{group.tickets.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
